import axios from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import type { UserManager } from "oidc-client-ts";

import { getValidIdToken } from "./idToken";

type CreateAxiosClientParams = Readonly<{
  /**
   * Optional because server-side rendering has no access to the browser-only OIDC runtime.
   * When omitted, this client will NOT attach Authorization headers or attempt silent renew.
   */
  userManager?: UserManager | null;
  baseURL?: string;
  // Called when we can’t recover (e.g., refresh token expired); should trigger interactive login.
  onAuthFailure: () => void;
  // Optional error sink (must remain console.error-only at call site).
  onError?: (error: unknown) => void;
}>;

function hasAuthorizationHeader(config: InternalAxiosRequestConfig): boolean {
  const headers = config.headers;
  if (!headers) return false;
  const existing =
    (typeof headers.get === "function" ? headers.get("Authorization") : undefined) ??
    (typeof headers === "object" && "Authorization" in headers ? (headers as Record<string, unknown>)["Authorization"] : undefined);
  return typeof existing === "string" && existing.trim().length > 0;
}

export function createAxiosClient(params: CreateAxiosClientParams): AxiosInstance {
  const { userManager, baseURL, onAuthFailure, onError } = params;

  const client = axios.create({
    baseURL,
  });

  // SSR-safe mode: no browser-only OIDC available.
  if (!userManager) {
    client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        if (status === 401) onAuthFailure();
        return Promise.reject(error);
      }
    );
    return client;
  }

  // Single-flight renew per axios instance to avoid stampedes on concurrent 401s.
  let renewInFlight: Promise<boolean> | null = null;

  const renew = async (): Promise<boolean> => {
    if (renewInFlight) return renewInFlight;
    renewInFlight = (async () => {
      try {
        const renewed = await userManager.signinSilent();
        return Boolean(renewed && renewed.id_token);
      } catch (error) {
        onError?.(error);
        return false;
      } finally {
        renewInFlight = null;
      }
    })();
    return renewInFlight;
  };

  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // If caller already provided Authorization, respect it.
      if (hasAuthorizationHeader(config)) return config;

      try {
        const token = await getValidIdToken(userManager);
        if (token) {
          config.headers.set?.("Authorization", `Bearer ${token}`);
          if (!config.headers.set) {
            // AxiosHeaders might not be present (older header shape); set as plain object.
            (config.headers as unknown as Record<string, string>)["Authorization"] = `Bearer ${token}`;
          }
        }
      } catch (error) {
        onError?.(error);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

      const status = error.response?.status;
      if (!originalRequest || status !== 401) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        // Already retried once; avoid loops.
        onAuthFailure();
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      const renewed = await renew();
      if (!renewed) {
        onAuthFailure();
        return Promise.reject(error);
      }

      // Retry with the new token.
      const token = await getValidIdToken(userManager);
      if (!token) {
        onAuthFailure();
        return Promise.reject(error);
      }

      originalRequest.headers = {
        ...(originalRequest.headers ?? {}),
        Authorization: `Bearer ${token}`,
      };

      return client(originalRequest);
    }
  );

  return client;
}

